/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import {
  AccordionGroup,
  AccordionHeader,
  AccordionPanel,
  Grid,
  ShowMoreList,
  Typography,
  breakpoints,
  typography2,
} from '@coursera/cds-core';
import type { AccordionName } from '@coursera/event-pulse-types';

import Markdown from 'bundles/cms/components/Markdown';
import type { DdpFaqFragment as Faq } from 'bundles/degree-description/__generated__/contentfulTypes';
import FaqModuleAccordionItem from 'bundles/degree-description/components/modules/faqs/FaqModuleAccordionItem';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import FaqsSchemaMarkup from 'bundles/seo/components/FaqsSchemaMarkup';

import _t from 'i18n!nls/degree-description';

// Show only top three FAQs https://coursera.atlassian.net/browse/ACQU-1457
const MAX_FAQS_TO_SHOW = 3;

type Props = {
  header: string;
  faqItems?: Partial<Faq | null>[];
  sideColumnContent?: React.ReactElement;
  accordionTrackingName?: string;
  accordionEventingV3TrackingName?: AccordionName;
  linkTrackingName?: string;
  enableShowMoreList?: boolean;
};

const styles = {
  container: css`
    padding-bottom: var(--cds-spacing-400);
    padding-top: var(--cds-spacing-600);
  `,
  block: css`
    background-color: var(--cds-color-neutral-background-primary);
    padding: var(--cds-spacing-400);
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 8px;
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200);
      border: none;
    }
  `,
  accordionGroup: (theme: DegreeDescriptionPageTheme) => css`
    .rc-Markdown.styled {
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      li {
        font-family: ${theme.typography.fontFamily};
      }

      p,
      li {
        ${typography2.bodyPrimary};
        ${theme.mobileFontSizeStyle?.body};
      }

      a {
        color: var(--cds-color-blue-800);

        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-900);
        }
      }
    }
  `,
  accordionGroupDivider: css`
    & > div {
      margin-top: 0 !important;

      &:not(:first-of-type) {
        border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      }
      ${breakpoints.down('sm')} {
        margin-top: var(--cds-spacing-100) !important;
        padding-top: var(--cds-spacing-100);

        &:first-of-type {
          border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
        }
      }
    }
  `,
  margin: (value: string) => css`
    margin: ${value};
  `,
  faqPadding: css`
    padding-top: var(--cds-spacing-300);
  `,
  showMoreList: css`
    .cds-ShowMoreList-listContainer,
    .cds-ShowMoreList-collapseContainer > div > div {
      & > div {
        margin-top: 0 !important;

        &:not(:first-of-type) {
          border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
        }
        ${breakpoints.down('sm')} {
          margin-top: var(--cds-spacing-100) !important;
          padding-top: var(--cds-spacing-100);

          &:first-of-type {
            border-top: 1px solid var(--cds-color-neutral-stroke-primary-weak);
          }
        }
      }
    }

    .cds-ShowMoreList-listContainer {
      padding: var(--cds-spacing-50);
    }

    .cds-ShowMoreList-collapseContainer {
      ${breakpoints.down('sm')} {
        border-top: none !important;
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
    }
  `,
};

type FaqItem = Partial<Faq> & {
  question: string;
};

const FaqModule = ({
  header,
  sideColumnContent,
  faqItems,
  accordionTrackingName,
  accordionEventingV3TrackingName,
  linkTrackingName,
  enableShowMoreList,
}: Props) => {
  const theme = useDegreeDescriptionPageTheme();
  const { id } = useDegreeDescriptionPageData();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const degreeId = id.replace(/^base~/, '');
  const pageSectionRef = useGetViewPageSectionRef('faq');

  if (!faqItems) return null;

  const filteredFaqItems = faqItems.filter((item): item is FaqItem => item !== null && Boolean(item.question));

  const faqs = filteredFaqItems.map((item) => {
    return (
      <FaqModuleAccordionItem
        trackingName={accordionTrackingName ?? 'faq'}
        eventingV3TrackingData={{
          name: accordionEventingV3TrackingName ?? 'faq',
          header: item.question,
          body: item.answer ?? '',
        }}
        trackingSection="faq"
        key={item.question}
      >
        <AccordionHeader label={item.question} supportText={item.subtitle ?? undefined} />
        <AccordionPanel>
          <Markdown
            source={item.answer}
            trackingName={linkTrackingName ?? 'faq_link'}
            buttonTrackingNameV3="degree_info_link"
            trackingSection="faq"
            trackingProductId={degreeId}
          />
        </AccordionPanel>
      </FaqModuleAccordionItem>
    );
  });

  return (
    <div ref={pageSectionRef}>
      <Container css={styles.container}>
        <FaqsSchemaMarkup faqs={filteredFaqItems} />
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h1semibold" css={theme.mobileFontSizeStyle.header} component="h2">
          {header}
        </Typography>
        <Grid
          css={styles.faqPadding}
          container
          spacing={{
            xs: 12,
            sm: 16,
            md: 32,
          }}
        >
          <Grid item xs={12} md={sideColumnContent ? 8 : 12}>
            <div css={styles.block}>
              <AccordionGroup css={[styles.accordionGroup(theme), !enableShowMoreList && styles.accordionGroupDivider]}>
                {enableShowMoreList ? (
                  <ShowMoreList
                    numItemsToShow={MAX_FAQS_TO_SHOW}
                    controllerProps={{ itemTypePlural: _t('frequently asked questions') }}
                    css={[styles.showMoreList]}
                  >
                    {faqs}
                  </ShowMoreList>
                ) : (
                  faqs
                )}
              </AccordionGroup>
            </div>
          </Grid>
          {sideColumnContent && (
            <Grid item xs={12} md={4}>
              {sideColumnContent}
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default React.memo(FaqModule);
